import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { Check, Sparkles, Waypoints } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageHeader } from "@/components/Common";
import { DevicePicker, TableList, useSelectedDevice } from "@/components/DevicePicker";
import { api, errorText, fetcher } from "@/lib/api";

const FIELDS = [
  { key: "timestamp", label: "Timestamp", hint: "When the event or count happened", words: ["detect", "event_time", "timestamp", "created", "time", "date", "ts"] },
  { key: "camera_id", label: "Camera / sensor", hint: "Which camera or detector produced it", words: ["camera", "cam", "sensor", "device", "detector", "station", "site"] },
  { key: "lane", label: "Lane", hint: "Lane number or name", words: ["lane"] },
  { key: "direction", label: "Direction", hint: "Travel direction, used for wrong-way", words: ["direction", "dir", "heading", "bound"] },
  { key: "speed", label: "Speed", hint: "Vehicle or average speed", words: ["speed", "spd", "velocity", "kmh", "mph"] },
  { key: "event_type", label: "Event type", hint: "Accident, stopped, slow, wrong-way…", words: ["event_type", "event", "type", "alarm", "incident", "category"] },
  { key: "vehicle_class", label: "Vehicle class", hint: "Car, truck, bus, motorcycle…", words: ["class", "vehicle", "veh", "category"] },
];
const NONE = "__none";
const PURPOSES = [
  { v: "detections", l: "Incident detections" },
  { v: "counts", l: "Vehicle counts" },
  { v: "speeds", l: "Speed readings" },
  { v: "other", l: "Other" },
];

function suggest(columns) {
  const out = {};
  const used = new Set();
  FIELDS.forEach((f) => {
    const match = f.words.map((w) => columns.find((c) => !used.has(c.name) && c.name.toLowerCase().includes(w))).find(Boolean);
    if (match) {
      out[f.key] = match.name;
      used.add(match.name);
    }
  });
  return out;
}

function MappingEditor({ deviceId, t, existing, onSaved }) {
  const [fields, setFields] = useState({});
  const [purpose, setPurpose] = useState("detections");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setFields(existing?.fields || {});
    setPurpose(existing?.purpose || "detections");
    setNotes(existing?.notes || "");
  }, [existing, t.db, t.tbl]);
  const { data: sample } = useSWR(`/explorer/rows?device_id=${deviceId}&db_name=${encodeURIComponent(t.db)}&table=${encodeURIComponent(t.tbl)}&limit=1`, fetcher);
  const row = sample?.rows?.[0]?.data;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/mappings", { device_id: deviceId, db: t.db, table: t.tbl, purpose, notes, fields });
      toast.success("Mapping saved");
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-mono text-base font-semibold md:text-lg">{t.db}.{t.tbl}</h2>
          <p className="text-xs text-muted-foreground">Tell VIDS what each column means. Detection rules in the next phase use this.</p>
        </div>
        <Button variant="secondary" size="sm" className="rounded-full" onClick={() => { setFields(suggest(t.columns || [])); toast("Suggested from column names. Check before saving."); }}
          data-testid="field-mapping-autodetect-button"><Sparkles className="mr-1.5 h-4 w-4" /> Suggest</Button>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <div className="text-sm font-medium">This table contains</div>
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger className="w-64 rounded-xl" data-testid="field-mapping-purpose-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {FIELDS.map((f) => (
          <div key={f.key} className="rounded-xl border border-border/70 p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{f.label}</div>
              {fields[f.key] && row && <span className="max-w-[45%] truncate font-mono text-[11px] text-muted-foreground" title={String(row[fields[f.key]])}>e.g. {String(row[fields[f.key]] ?? "null")}</span>}
            </div>
            <div className="mb-2 text-xs text-muted-foreground">{f.hint}</div>
            <Select value={fields[f.key] || NONE} onValueChange={(v) => setFields({ ...fields, [f.key]: v === NONE ? null : v })}>
              <SelectTrigger className="rounded-xl font-mono text-xs" data-testid={`field-mapping-select-${f.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not in this table</SelectItem>
                {(t.columns || []).map((c) => <SelectItem key={c.name} value={c.name} className="font-mono text-xs">{c.name} <span className="text-muted-foreground">({c.type})</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
        <div className="space-y-1.5 md:col-span-2">
          <div className="text-sm font-medium">Notes</div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} placeholder="e.g. direction 1 = northbound, speed in km/h" className="rounded-xl" data-testid="field-mapping-notes-input" />
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="field-mapping-save-button">Save mapping</Button>
      </div>
    </div>
  );
}

export default function Mapping() {
  const { devices, loaded, deviceId, setDeviceId } = useSelectedDevice();
  const { data: tables } = useSWR(deviceId ? `/explorer/tables?device_id=${deviceId}` : null, fetcher);
  const { data: mappings, mutate } = useSWR(deviceId ? `/mappings?device_id=${deviceId}` : null, fetcher);
  const [sel, setSel] = useState(null);
  const current = sel && tables?.find((t) => t.db === sel.db && t.tbl === sel.tbl);
  const mapped = useMemo(() => new Map((mappings || []).map((m) => [`${m.db}.${m.tbl}`, m])), [mappings]);
  return (
    <div>
      <PageHeader title="Field mapping" subtitle="Mark which tables and columns hold detections, counts and speeds, so incident rules can use them.">
        <DevicePicker devices={devices} deviceId={deviceId} onChange={(v) => { setDeviceId(v); setSel(null); }} />
      </PageHeader>
      {loaded && (!deviceId || (tables && tables.length === 0)) ? (
        <EmptyState icon={Waypoints} title="Nothing to map yet" testId="mapping-empty-state" text="Tables appear once the Edge Sender has connected and sent its table list." />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
          <TableList tables={tables || []} selected={current} onSelect={setSel}
            renderMeta={(t) => (mapped.has(`${t.db}.${t.tbl}`) ? <Check className="h-3.5 w-3.5" /> : null)} />
          {current ? <MappingEditor deviceId={deviceId} t={current} existing={mapped.get(`${current.db}.${current.tbl}`)} onSaved={mutate} />
            : <EmptyState icon={Waypoints} title="Select a table" text={`${mapped.size} of ${tables?.length || 0} tables mapped.`} testId="mapping-select-prompt" />}
        </div>
      )}
    </div>
  );
}
